(() => {
  const SELECTOR = '[data-bestek-review-wall]';
  const DRAG_START_THRESHOLD = 6;
  const LINK_CANCEL_THRESHOLD = 8;
  const COMMIT_DISTANCE_RATIO = 0.18;
  const COMMIT_VELOCITY = 0.35;
  const CLICK_GUARD_DURATION = 450;

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function initialise(root) {
    if (!root || root.dataset.bestekReviewReady === 'true') return;

    const track = root.querySelector('[data-bestek-review-track]');
    const slides = Array.from(root.querySelectorAll('[data-bestek-review-slide]'));
    const previous = root.querySelector('[data-bestek-review-prev]');
    const next = root.querySelector('[data-bestek-review-next]');
    const current = root.querySelector('[data-bestek-review-current]');

    if (!track || slides.length < 1 || !previous || !next || !current) return;

    root.dataset.bestekReviewReady = 'true';
    let activeIndex = 0;
    let frame = null;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartScrollLeft = 0;
    let pointerStartIndex = 0;
    let maximumPointerTravel = 0;
    let dragStarted = false;
    let velocitySamples = [];
    let savedInlineStyles = null;
    let blockLinkClicksUntil = 0;

    function closestIndex() {
      const trackLeft = track.getBoundingClientRect().left;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const candidate = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (candidate < distance) {
          nearest = index;
          distance = candidate;
        }
      });

      return nearest;
    }

    function update() {
      activeIndex = closestIndex();
      current.textContent = String(activeIndex + 1);
      previous.disabled = activeIndex === 0;
      next.disabled = activeIndex === slides.length - 1;
      root.style.setProperty('--review-progress', String((activeIndex + 1) / slides.length));
      slides.forEach((slide, index) => slide.toggleAttribute('aria-current', index === activeIndex));
    }

    function queueUpdate() {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        update();
      });
    }

    function moveTo(index) {
      const target = Math.max(0, Math.min(slides.length - 1, index));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const trackLeft = track.getBoundingClientRect().left;
      const targetLeft = track.scrollLeft + slides[target].getBoundingClientRect().left - trackLeft;
      track.scrollTo({ left: targetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    function recordVelocitySample(clientX, time) {
      velocitySamples.push({ clientX, time });

      const cutoff = time - 120;
      while (velocitySamples.length > 2 && velocitySamples[0].time < cutoff) {
        velocitySamples.shift();
      }
    }

    function releaseVelocity(clientX, time) {
      recordVelocitySample(clientX, time);
      if (velocitySamples.length < 2) return 0;

      const first = velocitySamples[0];
      const last = velocitySamples[velocitySamples.length - 1];
      const elapsed = last.time - first.time;

      return elapsed > 0 ? (first.clientX - last.clientX) / elapsed : 0;
    }

    function beginDrag() {
      if (dragStarted) return;

      dragStarted = true;
      savedInlineStyles = {
        cursor: track.style.cursor,
        scrollBehavior: track.style.scrollBehavior,
        scrollSnapType: track.style.scrollSnapType,
        userSelect: track.style.userSelect,
        webkitUserSelect: track.style.webkitUserSelect
      };

      track.classList.add('is-dragging');
      track.style.cursor = 'grabbing';
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.style.userSelect = 'none';
      track.style.webkitUserSelect = 'none';

      if (typeof track.setPointerCapture === 'function') {
        try {
          track.setPointerCapture(pointerId);
        } catch (_error) {
          // The window-level listeners below still keep the drag reliable.
        }
      }
    }

    function restoreAfterDrag() {
      track.classList.remove('is-dragging');

      if (!savedInlineStyles) return;
      track.style.cursor = savedInlineStyles.cursor;
      track.style.scrollBehavior = savedInlineStyles.scrollBehavior;
      track.style.scrollSnapType = savedInlineStyles.scrollSnapType;
      track.style.userSelect = savedInlineStyles.userSelect;
      track.style.webkitUserSelect = savedInlineStyles.webkitUserSelect;
      savedInlineStyles = null;
    }

    function slideStep() {
      if (slides.length > 1) {
        return Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
      }

      return slides[0].getBoundingClientRect().width;
    }

    function resetPointerState() {
      pointerId = null;
      maximumPointerTravel = 0;
      dragStarted = false;
      velocitySamples = [];
    }

    function finishDrag(event, cancelled = false) {
      if (pointerId === null || (event.pointerId !== undefined && event.pointerId !== pointerId)) return;

      const now = performance.now();
      const clientX = Number.isFinite(event.clientX) ? event.clientX : pointerStartX;
      maximumPointerTravel = Math.max(maximumPointerTravel, Math.abs(clientX - pointerStartX));

      const wasDragging = dragStarted;
      const completedPointerId = pointerId;
      const distance = track.scrollLeft - pointerStartScrollLeft;
      const velocity = cancelled ? 0 : releaseVelocity(clientX, now);
      let targetIndex = closestIndex();

      if (wasDragging) {
        const cardWidth = slides[pointerStartIndex].getBoundingClientRect().width || slideStep();
        const passesDistance = Math.abs(distance) > cardWidth * COMMIT_DISTANCE_RATIO;
        const passesVelocity = Math.abs(velocity) > COMMIT_VELOCITY;

        if (!cancelled && (passesDistance || passesVelocity)) {
          const direction = Math.sign(Math.abs(distance) >= 2 ? distance : velocity);
          const steps = Math.max(1, Math.round(Math.abs(distance) / Math.max(1, slideStep())));
          targetIndex = clamp(pointerStartIndex + direction * steps, 0, slides.length - 1);
        } else if (!cancelled) {
          targetIndex = pointerStartIndex;
        }

        if (maximumPointerTravel > LINK_CANCEL_THRESHOLD) {
          blockLinkClicksUntil = now + CLICK_GUARD_DURATION;
        }
      }

      resetPointerState();
      restoreAfterDrag();

      if (typeof track.hasPointerCapture === 'function' && track.hasPointerCapture(completedPointerId)) {
        try {
          track.releasePointerCapture(completedPointerId);
        } catch (_error) {
          // Capture may already have been released by the browser.
        }
      }

      if (wasDragging) {
        event.preventDefault();
        moveTo(targetIndex);
      } else {
        queueUpdate();
      }
    }

    function handlePointerDown(event) {
      if (
        pointerId !== null ||
        event.pointerType === 'touch' ||
        event.isPrimary === false ||
        event.button !== 0
      ) {
        return;
      }

      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartScrollLeft = track.scrollLeft;
      pointerStartIndex = closestIndex();
      maximumPointerTravel = 0;
      dragStarted = false;
      velocitySamples = [];
      recordVelocitySample(event.clientX, performance.now());
    }

    function handlePointerMove(event) {
      if (pointerId === null || event.pointerId !== pointerId) return;

      const distance = event.clientX - pointerStartX;
      maximumPointerTravel = Math.max(maximumPointerTravel, Math.abs(distance));
      recordVelocitySample(event.clientX, performance.now());

      if (!dragStarted && maximumPointerTravel >= DRAG_START_THRESHOLD) {
        beginDrag();
      }

      if (!dragStarted) return;

      event.preventDefault();
      const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      track.scrollLeft = clamp(pointerStartScrollLeft - distance, 0, maximumScroll);
      queueUpdate();
    }

    function guardProductLink(event) {
      if (performance.now() > blockLinkClicksUntil || !event.target.closest('a')) return;

      event.preventDefault();
      event.stopPropagation();
      blockLinkClicksUntil = 0;
    }

    previous.addEventListener('click', () => moveTo(activeIndex - 1));
    next.addEventListener('click', () => moveTo(activeIndex + 1));
    track.addEventListener('scroll', queueUpdate, { passive: true });
    track.addEventListener('pointerdown', handlePointerDown);
    track.addEventListener('lostpointercapture', event => finishDrag(event, true));
    track.addEventListener('click', guardProductLink, true);
    track.addEventListener('dragstart', event => event.preventDefault());
    track.addEventListener('selectstart', event => {
      if (dragStarted) event.preventDefault();
    });
    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', event => finishDrag(event));
    window.addEventListener('pointercancel', event => finishDrag(event, true));
    track.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      moveTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    window.addEventListener('resize', queueUpdate, { passive: true });
    update();
  }

  function initialiseAll(container = document) {
    container.querySelectorAll(SELECTOR).forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', event => initialiseAll(event.target));
})();
